import React, { useEffect, useState } from "react";
import { Modal } from "rsuite";
import { MdCalendarMonth, MdEdit, MdClose, MdCheck } from "react-icons/md";
import { FiUser, FiMail, FiBriefcase, FiShield } from "react-icons/fi";
import DatePicker from "react-datepicker";
import { ptBR } from "date-fns/locale";
import { toast } from "react-toastify";
import api from "../../../../services/axios";
import ProfileSelect from "./ProfileSelect";

const FIELD_CLASS =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition-all focus:border-dv-blue-500 focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";

const FIELD_CLASS_DISABLED =
  "w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-navy-700 dark:border-white/10 dark:bg-navy-900 dark:text-white";

const LABEL_CLASS =
  "mb-1 flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300";

function getInitials(name: string): string {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function formatDateDisplay(value: string | undefined): string {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("pt-BR");
  } catch {
    return value;
  }
}

const ModalViewDetailUser: React.FC<any> = ({
  openUserDetailModal,
  setOpenUserDetailModal,
  details,
  updateState,
}) => {
  const [detail, setDetail] = useState<any>({});
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  // editable fields
  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [dtOfBirth, setDtOfBirth] = useState<Date | null>(null);
  const [dtContract, setDtContract] = useState<Date | null>(null);
  const [profileId, setProfileId] = useState("");
  const [profileName, setProfileName] = useState<string | null>(null);
  const [flActive, setFlActive] = useState(false);

  useEffect(() => {
    if (details) {
      setDetail(details);
      setUserName(details.userName ?? "");
      setEmail(details.email ?? "");
      setJobDescription(details.jobDescription ?? "");
      setDtOfBirth(details.dtOfBirth ? new Date(details.dtOfBirth) : null);
      setDtContract(details.dtContract ? new Date(details.dtContract) : null);
      setProfileId(details.profileId != null ? String(details.profileId) : "");
      setFlActive(details.flActive ?? false);
    }
    setIsEditing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openUserDetailModal, details]);

  const handleClose = () => {
    setIsEditing(false);
    setOpenUserDetailModal(false);
  };

  const handleCancelEdit = () => {
    // reset to original values
    setUserName(detail.userName ?? "");
    setEmail(detail.email ?? "");
    setJobDescription(detail.jobDescription ?? "");
    setDtOfBirth(detail.dtOfBirth ? new Date(detail.dtOfBirth) : null);
    setDtContract(detail.dtContract ? new Date(detail.dtContract) : null);
    setProfileId(detail.profileId != null ? String(detail.profileId) : "");
    setFlActive(detail.flActive ?? false);
    setIsEditing(false);
  };

  const originalProfileId = detail?.profileId != null ? String(detail.profileId) : "";

  const handleSave = async () => {
    try {
      setLoading(true);
      const payload = {
        nmEmployee: userName,
        email,
        jobDescription,
        dtOfBirth: dtOfBirth ? dtOfBirth.toISOString() : null,
        dtContract: dtContract ? dtContract.toISOString() : null,
        flActive,
        // Só vai quando mudou: reenviar o perfil atual seria uma atribuição (e checagem de escalada) à toa.
        ...(profileId !== originalProfileId
          ? { profileId: profileId === "" ? null : Number(profileId) }
          : {}),
      };
      await api.put(`/Employee/employees/${detail.id}`, payload);
      toast.success("Usuário atualizado com sucesso!", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setDetail({ ...detail, ...payload, ...(profileName ? { profileName } : {}) });
      setIsEditing(false);
      if (updateState) updateState();
    } catch (error: any) {
      console.error("Error updating user:", error);
      toast.error(
        error?.response?.data?.message ??
          "Falha ao atualizar usuário. Tente novamente.",
        {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const isSaveDisabled =
    loading ||
    userName.trim().length < 2 ||
    email.trim().length < 5 ||
    jobDescription.trim().length < 2;

  return (
    <>
      <Modal open={openUserDetailModal} size={"lg"} onClose={handleClose}>
        <Modal.Header className="border-b border-gray-100 pb-3 dark:border-navy-700">
          <Modal.Title>
            <span className="text-lg font-bold text-navy-700 dark:text-white">
              Detalhes do usuário
            </span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="py-2">
            {/* ── Avatar + name header ─────────────────────────────── */}
            <div className="dv-aura mb-6 flex items-center gap-4 rounded-2xl bg-dv-gradient p-5 text-white shadow-dv-glow">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-white/20 text-2xl font-bold backdrop-blur-sm">
                {getInitials(detail?.userName ?? "")}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xl font-bold">
                  {detail?.userName ?? "—"}
                </p>
                <p className="truncate text-sm text-white/80">
                  {detail?.email ?? "—"}
                </p>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-medium">
                    {detail?.profileName ?? "Sem perfil"}
                  </span>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      detail?.flActive
                        ? "bg-emerald-400/30 text-emerald-50"
                        : "bg-white/20 text-white/90"
                    }`}
                  >
                    {detail?.flActive ? "Ativo" : "Inativo"}
                  </span>
                </div>
              </div>
              {!isEditing && (
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  aria-label="Editar usuário"
                  title="Editar usuário"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/20 transition hover:bg-white/30 active:scale-[.98]"
                >
                  <MdEdit className="text-xl" aria-hidden="true" />
                </button>
              )}
            </div>

            {/* ── Fields grid ──────────────────────────────────────── */}
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              {/* Name */}
              <div className="flex flex-col">
                <label htmlFor="detail-user-name" className={LABEL_CLASS}>
                  <FiUser className="text-dv-blue-500" aria-hidden="true" /> Nome
                </label>
                {isEditing ? (
                  <input
                    id="detail-user-name"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    className={FIELD_CLASS}
                    placeholder="Nome completo"
                  />
                ) : (
                  <p className={FIELD_CLASS_DISABLED}>{detail?.userName ?? "—"}</p>
                )}
              </div>

              {/* Email */}
              <div className="flex flex-col">
                <label htmlFor="detail-user-email" className={LABEL_CLASS}>
                  <FiMail className="text-dv-blue-500" aria-hidden="true" /> E-mail
                </label>
                {isEditing ? (
                  <input
                    id="detail-user-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={FIELD_CLASS}
                    placeholder="email@exemplo.com"
                  />
                ) : (
                  <p className={FIELD_CLASS_DISABLED}>{detail?.email ?? "—"}</p>
                )}
              </div>

              {/* Job Description */}
              <div className="flex flex-col sm:col-span-2">
                <label htmlFor="detail-user-job" className={LABEL_CLASS}>
                  <FiBriefcase className="text-dv-blue-500" aria-hidden="true" /> Cargo
                </label>
                {isEditing ? (
                  <input
                    id="detail-user-job"
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                    className={FIELD_CLASS}
                    placeholder="ex.: Engenheiro de Software"
                  />
                ) : (
                  <p className={FIELD_CLASS_DISABLED}>
                    {detail?.jobDescription ?? "—"}
                  </p>
                )}
              </div>

              {/* Birth Date */}
              <div className="flex flex-col">
                <label htmlFor="detail-user-birth" className={LABEL_CLASS}>
                  <MdCalendarMonth className="text-dv-blue-500" aria-hidden="true" /> Data de nascimento
                </label>
                {isEditing ? (
                  <DatePicker
                    id="detail-user-birth"
                    showIcon
                    selected={dtOfBirth}
                    locale={ptBR}
                    icon={<MdCalendarMonth className="text-dv-blue-500 text-xl" />}
                    dateFormat="dd/MM/yyyy"
                    onChange={(date) => setDtOfBirth(date)}
                    placeholderText="dd/mm/aaaa"
                    className={`${FIELD_CLASS} w-full`}
                  />
                ) : (
                  <p className={`${FIELD_CLASS_DISABLED} tabular-nums`}>
                    {formatDateDisplay(detail?.dtOfBirth)}
                  </p>
                )}
              </div>

              {/* Contract Date */}
              <div className="flex flex-col">
                <label htmlFor="detail-user-contract" className={LABEL_CLASS}>
                  <MdCalendarMonth className="text-dv-blue-500" aria-hidden="true" /> Data de contratação
                </label>
                {isEditing ? (
                  <DatePicker
                    id="detail-user-contract"
                    showIcon
                    selected={dtContract}
                    locale={ptBR}
                    icon={<MdCalendarMonth className="text-dv-blue-500 text-xl" />}
                    dateFormat="dd/MM/yyyy"
                    onChange={(date) => setDtContract(date)}
                    placeholderText="dd/mm/aaaa"
                    className={`${FIELD_CLASS} w-full`}
                  />
                ) : (
                  <p className={`${FIELD_CLASS_DISABLED} tabular-nums`}>
                    {formatDateDisplay(detail?.dtContract)}
                  </p>
                )}
              </div>

              {/* Perfil */}
              <div className="flex flex-col">
                <label htmlFor="detail-user-profile" className={LABEL_CLASS}>
                  <FiShield className="text-dv-blue-500" aria-hidden="true" /> Perfil
                </label>
                {isEditing ? (
                  <ProfileSelect
                    id="detail-user-profile"
                    value={profileId}
                    onChange={(id, name) => {
                      setProfileId(id);
                      setProfileName(name ?? null);
                    }}
                    personName={userName}
                    className={FIELD_CLASS}
                  />
                ) : (
                  <p className={FIELD_CLASS_DISABLED}>{detail?.profileName ?? "—"}</p>
                )}
              </div>

              {/* Status */}
              <div className="flex flex-col">
                <span className={LABEL_CLASS}>Status</span>
                {isEditing ? (
                  <button
                    type="button"
                    aria-label={flActive ? "Desativar usuário" : "Ativar usuário"}
                    onClick={() => setFlActive((prev) => !prev)}
                    className={`flex h-[42px] w-full items-center justify-between rounded-xl border px-3 text-sm font-semibold transition ${
                      flActive
                        ? "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                        : "border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-600 dark:bg-navy-900 dark:text-gray-300"
                    }`}
                  >
                    <span>{flActive ? "Ativo" : "Inativo"}</span>
                    <span
                      aria-hidden="true"
                      className={`h-5 w-10 rounded-full transition-colors ${
                        flActive ? "bg-emerald-400" : "bg-gray-300 dark:bg-navy-600"
                      } relative inline-flex items-center`}
                    >
                      <span
                        className={`absolute h-4 w-4 rounded-full bg-white shadow transition-transform ${
                          flActive ? "translate-x-5" : "translate-x-0.5"
                        }`}
                      />
                    </span>
                  </button>
                ) : (
                  <span
                    className={`inline-flex w-fit items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                      detail?.flActive
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                        : "bg-gray-100 text-gray-700 dark:bg-navy-700 dark:text-gray-300"
                    }`}
                  >
                    {detail?.flActive ? "Ativo" : "Inativo"}
                  </span>
                )}
              </div>

              {/* Created at (read-only always) */}
              <div className="flex flex-col sm:col-span-2">
                <span className={LABEL_CLASS}>Criado em</span>
                <p className={`${FIELD_CLASS_DISABLED} tabular-nums`}>
                  {formatDateDisplay(detail?.dtCreated)}
                </p>
              </div>
            </div>
          </div>
        </Modal.Body>

        {/* ── Footer ───────────────────────────────────────────────── */}
        <Modal.Footer className="border-t border-gray-100 pt-4 dark:border-navy-700">
          {isEditing ? (
            <div className="flex w-full gap-3">
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={loading}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] disabled:opacity-50 dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
              >
                <MdClose className="text-lg" aria-hidden="true" />
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaveDisabled}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
              >
                {loading ? (
                  <>
                    <svg
                      className="h-4 w-4 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z"
                      />
                    </svg>
                    Salvando…
                  </>
                ) : (
                  <>
                    <MdCheck className="text-lg" aria-hidden="true" />
                    Salvar alterações
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="flex w-full justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
              >
                Fechar
              </button>
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="flex items-center justify-center gap-1.5 rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
              >
                <MdEdit className="text-base" aria-hidden="true" />
                Editar usuário
              </button>
            </div>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};
export default ModalViewDetailUser;
